import React, { useEffect, useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { isoOf } from '../../utils/projectShoots';
import type { MirroredEvent } from '../../utils/calendarMirror';
import type { CalendarEntry } from '../../utils/calendarEntries';
import { composeWeek, monthWindow, type WeekSource } from '../../utils/weekAhead';
import type { Entry } from './CalendarMonth';

/**
 * The calendar, on a phone (24 Sep 2026).
 *
 * The month grid is seven columns wide and does not change below 640px, so
 * on a 390px screen each day is about fifty pixels across with event chips
 * inside it — unreadable, and untappable with a thumb. Tom, on what Portal
 * OS is like on a phone: the one screen that is genuinely broken.
 *
 * A grid is a shape for comparing a whole month at once. A phone cannot show
 * a whole month at once whatever you draw, so this stops pretending and
 * becomes a list: one row per day, down the month, in the order days happen.
 *
 * **The current month STARTS at today** rather than at the 1st, with the
 * days already gone folded behind one line. The first version scrolled to
 * today instead and got it wrong in a way worth recording: it scrolled on
 * first paint, while every row was still an empty one-line placeholder, and
 * then the data arrived and the rows above today grew. The page stayed where
 * it had been put and landed on the 13th. Tom saw the 13th.
 *
 * Any fix that keeps the scroll is a race against however long Supabase
 * takes; starting the list in the right place is not a race at all. That is
 * the rule worth keeping: put the content where it belongs rather than
 * moving the viewport to it.
 *
 * Two other deliberate differences from the Week screen, which composes its
 * rows with the same function:
 *
 *   • EMPTY DAYS ARE NOT COLLAPSED. On the week, a run of clear days is
 *     noise. Here a free day is the thing you are looking for — and the day
 *     you want to put something on — so every day keeps its own row and its
 *     own +.
 *   • THE ROWS DO SOMETHING. Tapping a shoot or a diary entry opens the same
 *     editors the grid opens, below. Google's own entries stay read-only and
 *     link out, exactly as the chips do — they are somebody else's.
 *
 * `focusDate` is the day pressed on the Week Ahead to get here. It is marked,
 * never scrolled to — same rule as above — and it unfolds the earlier days if
 * it happens to sit among them, because a mark on a row nobody can see is
 * worse than no mark at all.
 */

export function CalendarAgenda({
  month,
  entries,
  notes,
  events,
  selectedId,
  selectedNoteId,
  focusDate = '',
  onSelect,
  onSelectNote,
  onAdd
}: {
  month: Date;
  entries: Entry[];
  notes: CalendarEntry[];
  events: MirroredEvent[];
  selectedId: string;
  selectedNoteId: string;
  focusDate?: string;
  onSelect: (entry: Entry) => void;
  onSelectNote: (entry: CalendarEntry) => void;
  onAdd: (iso: string) => void;
}) {
  const today = isoOf(new Date());
  const monthKey = `${month.getFullYear()}-${month.getMonth()}`;
  const [showEarlier, setShowEarlier] = useState(false);

  /* Paging to another month and back starts folded again — "show me what has
     already happened" is a question about one month, not a setting. */
  useEffect(() => setShowEarlier(false), [monthKey]);
  const source: WeekSource = {
    shoots: entries.map(entry => ({
      shoot: entry.shoot,
      projectId: entry.projectId,
      client: entry.project?.client ?? '',
      project: entry.project?.name ?? ''
    })),
    entries: notes,
    events
  };
  const all = composeWeek(monthWindow(month), source, today);

  /* ISO dates compare correctly as plain strings, so no Date parsing and no
     timezone to get wrong. */
  const fromToday = all.findIndex(day => day.iso >= today);
  const foldable = fromToday > 0 ? fromToday : 0;
  /* A focused day among the earlier ones forces them open, rather than
     flipping the state the fold button owns — pressing "Earlier" is still the
     only thing that changes that setting. */
  const focusIsEarlier = focusDate !== '' && focusDate < today;
  const folded = foldable > 0 && !showEarlier && !focusIsEarlier;
  const days = folded ? all.slice(foldable) : all;
  const monthName = month.toLocaleDateString('en-GB', {
    month: 'long'
  });
  return <div className="mt-8 border-t border-ruleDark" data-id="element-497" data-mp="1i9wmndwvpf0x">
      {folded && <button type="button" onClick={() => setShowEarlier(true)} className="label-sm flex h-12 w-full items-center border-b border-ruleDark text-left text-paper/60 transition-colors duration-150 ease-out hover:text-paper" data-id="element-498" data-mp="1ib7rzi5z2uu5">
          Earlier in {monthName} &middot; {foldable} {foldable === 1 ? 'day' : 'days'}
        </button>}

      {days.map(day => {
      const focused = focusDate !== '' && day.iso === focusDate;
      return <div key={day.iso} className={`border-b border-ruleDark px-2 py-3 ${focused ? '-mx-2 bg-paper/[0.06]' : ''}`} data-id="element-499" data-mp="2upavfoonks0">
            <div className="flex items-center justify-between gap-4" data-id="element-500" data-mp="3vhe36263doma">
              <span className={`label-sm ${day.isToday || focused ? 'text-paper' : 'text-paper/60'}`} data-id="element-501" data-mp="3qobfganzx9e0">
                {day.label}
                {day.isToday && <span className="ml-3 text-paper/60" data-id="element-502" data-mp="3hq98zflhtojc">Today</span>}
              </span>
              <button type="button" onClick={() => onAdd(day.iso)} aria-label={`Add something on ${day.label}`} className="-mr-2 inline-flex h-11 w-11 items-center justify-center text-paper/45 transition-colors duration-150 ease-out hover:text-paper" data-id="element-503" data-mp="i8hxdre0npuu">
                <PlusIcon className="h-4 w-4" strokeWidth={1.5} data-id="element-504" data-mp="4bw64z8noz9e" />
              </button>
            </div>

            {day.items.length > 0 && <ul className="m-0 mt-1 list-none p-0" data-id="element-505" data-mp="2ozr90ng2g3a5">
                {day.items.map(item => {
            const selected = item.kind === 'shoot' && item.sourceId === selectedId && selectedId !== '' || item.kind === 'entry' && item.sourceId === selectedNoteId && selectedNoteId !== '';
            const edge = selected ? 'border-paper' : 'border-transparent';
            const tone = item.quiet ? 'text-paper/60' : 'text-paper';
            const body = `block w-full border-l pl-4 py-2 text-left text-[1.0625rem] leading-snug ${edge} ${tone}`;
            if (item.kind === 'google') {
              const event = item.sourceId ? events.find(row => row.eventId === item.sourceId) : undefined;
              /* Somebody else's entry. Read-only here forever — the link
                 out is the honest affordance. */
              return <li key={item.key} data-id="element-506" data-mp="1tqdxysdxmewq">
                        {event?.htmlLink ? <a href={event.htmlLink} target="_blank" rel="noopener noreferrer" className={body} data-id="element-507" data-mp="1a5wyd98vt9b2">
                            {item.text}
                          </a> : <span className={body} data-id="element-508" data-mp="1t3zmzmxip48c">{item.text}</span>}
                      </li>;
            }
            const open = () => {
              if (item.kind === 'shoot') {
                const found = entries.find(row => row.shoot.id === item.sourceId);
                if (found) onSelect(found);
                return;
              }
              const note = notes.find(row => row.id === item.sourceId);
              if (note) onSelectNote(note);
            };
            return <li key={item.key} data-id="element-509" data-mp="3e8d3szx5b15f">
                      <button type="button" onClick={open} className={body} data-id="element-510" data-mp="3e7br6r0he6ll">
                        {item.text}
                      </button>
                    </li>;
          })}
              </ul>}
          </div>;
    })}
    </div>;
}